<script>
	import Survey, { Action, ComputedUpdater, Serializer, setLicenseKey, surveyLocalization } from "survey-core";
	import "survey-core/defaultV2.min.css";
	import "survey-core/survey.i18n";
	import { PropertyGridEditorCollection, editorLocalization, localization } from "survey-creator-core";
	import "survey-creator-core/i18n/german";
	import "survey-creator-core/i18n/italian";
	import "survey-creator-core/survey-creator-core.min.css";
	import { SurveyCreator } from "survey-creator-knockout";
	import { onMount } from 'svelte';
	import { generateGUID } from "../../../../functions";
	import { locale } from '../../../../services/i18n.js';
	import { hasRoleSysAdmin, hasRoleCompanyManager } from "../../../../stores.js";
	import * as SurveyUtils from "../SurveyUtils";
	import * as SurveyBuilderUtils from "./SurveyBuilderUtils";
	import Quill from 'quill';
    import 'quill/dist/quill.snow.css';

	export let stringifiedJson = '';
	let initialJson = {};
	export let isRestrictedEditMode = false;

	onMount(async () => {
		surveyLocalization.supportedLocales = ['it', 'de', 'en'];
		localization.currentLocale = $locale;
		surveyLocalization.defaultLocale = 'it';
		setLicenseKey(process.env.SURVEYJS_KEY);
		Serializer.findProperty('survey', 'locale').visible = false;
		Serializer.findProperty("checkbox", "hasSelectAll").visible = false;
		Serializer.findProperty("itemvalue", "value").isUnique = true;
		Serializer.findProperty("question", "description").visible = true;
		Serializer.findProperty("question", "description").html = true;
		Serializer.findProperty("question", "title").html = true;
		
		const componentName = "quill";

		function initQuill() {
			const quillStyle = document.createElement("style");
			quillStyle.innerHTML = `
				.ql-editor {
					background: #ffffff !important;
					color: #000000 !important;
				}
				
				.ql-editor p {
					color: #000000 !important;
				}
				
				.ql-editor * {
					color: #000000 !important;
				}
				
				.ql-editor.ql-blank::before {
					color: #999999 !important;
				}
			`;
			document.head.appendChild(quillStyle);
			const iconId = "icon-editor";
			var widget = {
				name: componentName,
				title: "Quill",
				iconName: iconId,
				widgetIsLoaded: function () {
					return typeof Quill !== "undefined";
				},
				isFit: function (question) {
					return question.getType() === componentName;
				},
				activatedByChanged: function (activatedBy) {
					Serializer.addClass(componentName, [], null, "empty");
					let registerQuestion = Survey.ElementFactory.Instance.registerCustomQuestion;
					if (!!registerQuestion) registerQuestion(componentName);
					Serializer.addProperty(componentName, {
						name: "height",
						default: "200px",
						category: "layout"
					});
				},
				htmlTemplate: "<div></div>",
				afterRender: function (question, el) {
					el.style.height = question.height;
					var editor = new Quill(el, {
						theme: "snow"
					});
					editor.enable(!question.isReadOnly);
					var isValueChanging = false;
					editor.on("text-change", function (eventName, ...args) {
						isValueChanging = true;
						question.value = editor.root.innerHTML;
						isValueChanging = false;
					});
					var updateValueHandler = function () {
						if (isValueChanging) return;
						const text = question.value || "";
						editor.root.innerHTML = text;
					};
					question.valueChangedCallback = updateValueHandler;
					question.readOnlyChangedCallback = function () {
						editor.enable(!question.isReadOnly);
					};
					updateValueHandler();
				},
				willUnmount: function (question, el) {}
			};
			Survey.CustomWidgetCollection.Instance.addCustomWidget(widget, "customtype");
		}

	if (!Serializer.findClass(componentName)) {
        initQuill();
    }

	PropertyGridEditorCollection.register({
        fit: function (prop) {
            return prop.name === "description" || prop.type === "text" || prop.type === "html";
        },
        getJSON: function () {
            return { type: "quill" };
        }
    });

	function applyHtml(_, options) {
        let str = options.text;
        if (str.indexOf("<p>") === 0) {
            str = str.substring(3);
            str = str.substring(0, str.length - 4);
        }
        options.html = str;
    }

		if ($hasRoleCompanyManager) {
			Serializer.findProperty("question", "name").visible = false;
		} else {
			Serializer.findProperty("question", "name").visible = true;
		}

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'adminComment',
			displayName: 'Commento amministratore (Visibile al Mobility Manager)',
			type: 'text',
			default: ' ', 
			category: 'general'
		});

		Serializer.findProperty('question', 'adminComment').html = true;

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'PSCL',
			type: 'boolean',
			default: false,
			category: 'general'
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'SmartWorking',
			type: 'boolean',
			default: false,
			category: 'general'
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'Appendix',
			displayName: 'Grafico in appendice',
			type: 'boolean',
			default: false,
			category: 'general'
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'isTimeRelated',
			displayName: 'Domanda relativa agli orari',
			type: 'boolean',
			default: false,
			category: 'general'
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'UnitOfMeasure',
			displayName: 'Unit of measure',
			choices: ["None", "Km", "Min"],
			visibleIf: SurveyUtils.isNumber
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'modifiabilitySettingsForCompanyManager',
			displayName: 'Impostazioni modificabilità per company manager',
			choices: ["Domanda non disattivabile né modificabile in alcun aspetto", "Alcune opzioni di risposta disattivabili", 
    					"Alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta", "Domanda disattivabile, alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta"],
			visibleIf: SurveyUtils.isChoices
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'deactivatableChoices',
			type: 'multiplevalues',
			displayName: 'Opzioni di risposta disattivabili',
			visibleIf: obj => SurveyUtils.isChoices(obj) && SurveyBuilderUtils.MODIFIABILITY_OPTIONS_FOR_DEACTIVATABLECHOICES.includes(obj.modifiabilitySettingsForCompanyManager),
			choices: obj => SurveyBuilderUtils.dynamicChoices(obj),
			default: []
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'modifiabilitySettingsForCompanyManagerMatrix',
			displayName: 'Impostazioni modificabilità per company manager',
			choices: ["Domanda non disattivabile né modificabile in alcun aspetto", "Alcune righe disattivabili"],
			visibleIf: SurveyUtils.isMatrix
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'deactivatableRows',
			type: 'multiplevalues',
			displayName: 'Righe disattivabili',
			visibleIf: obj => SurveyUtils.isMatrix(obj) && SurveyBuilderUtils.MODIFIABILITY_OPTIONS_MATRIX_FOR_DEACTIVATABLEROWS.includes(obj.modifiabilitySettingsForCompanyManagerMatrix),
			choices: obj => SurveyBuilderUtils.dynamicRows(obj),
			default: []
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'modifiabilitySettingsForCompanyManagerText',
			displayName: 'Impostazioni modificabilità per company manager',
			choices: ["Domanda non disattivabile né modificabile in alcun aspetto", "Domanda disattivabile"],
			visibleIf: SurveyUtils.isText
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'ChartTypeChoices',
			displayName: 'Chart type',
			choices: ["Bar", "Pie", "Doughnut", "Scatter"],
			visibleIf: SurveyUtils.isChoices
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'ChartTypeNumber',
			displayName: 'Chart type',
			choices: ["Vertical Bar", "Bar", "Scatter"],
			visibleIf: SurveyUtils.isNumber
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'ChartTypeMatrix',
			displayName: 'Chart type',
			choices:  ["Bar", "Pie", "Doughnut", "Scatter", "Stacked bar"],
			visibleIf: SurveyUtils.isMatrix
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'OtherColor',
			type: 'color',
			displayName: 'Specify color for "Other" option',
			category: 'choices',
			visibleIf: obj => obj.hasOther
		});

		SurveyBuilderUtils.addQuestionProperty(Serializer, {
			name: 'NewItemsColor',
			type: 'color',
			displayName: 'Specify default color for new options',
			category: 'choices'
		});

		SurveyBuilderUtils.addItemValueProperty(Serializer, {
			name: 'DisplayName',
			showMode: "form"
		});

		SurveyBuilderUtils.addItemValueProperty(Serializer, {
			name: 'Color',
			type: 'color',
			showMode: "form"
		});

		if (stringifiedJson) {
			initialJson = JSON.parse(stringifiedJson);
			stringifiedJson = JSON.parse(stringifiedJson);
			//stringifiedJson.locale = $locale;
			stringifiedJson.locale = 'it';
			stringifiedJson = JSON.stringify(stringifiedJson);
		}

		let creatorOptions = {
			showLogicTab: true,
			showTranslationTab: true
		};

		PropertyGridEditorCollection.register({
			fit: function(prop) {
				return prop.name === "otherText";
			},
			getJSON: function(obj,  prop,options) {
				return { type: "text"}
			}
		});

		let creator = new SurveyCreator(creatorOptions);
		creator.survey.onTextMarkdown.add(applyHtml);

		const translationsIT = editorLocalization.getLocale("it");
		translationsIT.pe.question.description = "Descrizione";
		translationsIT.qt.radiogroup = "Risposta singola";
		translationsIT.qt.checkbox = "Risposta multipla";
		translationsIT.pe.question.title = "Domanda";
		translationsIT.pe.otherText = "Altro (Descrivi)";
		translationsIT.pe.noneText = "Nessuna risposta";

		const translationsEN = editorLocalization.getLocale("en");
		translationsEN.pe.question.description = "Description";
		translationsEN.qt.radiogroup = "Single answer";
		translationsEN.qt.checkbox = "Multiple answers";
		translationsEN.pe.question.title = "Question";
		translationsEN.pe.otherText = "Other (Describe)";
		translationsEN.pe.noneText = "No answer";

		const translationsDE = editorLocalization.getLocale("de");
		translationsDE.pe.question.description = "Beschreibung";
		translationsDE.qt.radiogroup = "Einzelne Antwort";
		translationsDE.qt.checkbox = "Mehrfachantworten";
		translationsDE.pe.question.title = "Frage";
		translationsDE.pe.otherText = "Andere (Beschreiben)";
		translationsDE.pe.noneText = "Keine Antwort";

		const translations = editorLocalization.getLocale(localization.currentLocale);

		const surveyCreatorDiv = document.getElementById('surveyCreator');

		function renderAdminComment(question, htmlElement) {
			htmlElement.querySelectorAll(".admin-comment").forEach(el => el.remove());
			
			if (!question.adminComment?.trim()) return;
			
			const div = document.createElement("div");
			div.className = "admin-comment";
			div.innerHTML = `
				<div class="admin-comment-content">
					<i class="material-icons-outlined">info</i>
					<div class="admin-comment-body">${question.adminComment}</div>
				</div>`;
			
			htmlElement.insertBefore(div, htmlElement.firstChild);
		}

		// Funzione per aggiungere gli stili CSS (una sola volta)
		function addAdminCommentStyles() {
			if (!document.querySelector('style[data-admin-comment-styles]')) {
				const style = document.createElement("style");
				style.setAttribute('data-admin-comment-styles', 'true');
				style.innerHTML = `
					.admin-comment { 
						margin-bottom: 10px; 
					}
					.admin-comment-content {
						display: flex; 
						align-items: flex-start;
						padding: 8px 12px; 
						background: #fff3cd;
						border: 1px solid #ffeeba; 
						border-radius: 4px;
						color: #856404;
						width: 100%;
						box-sizing: border-box;
					}
					.admin-comment-content i { 
						margin-right: 8px; 
						font-size: 18px;
						flex-shrink: 0;
					}
					.admin-comment-body { 
						font-size: 14px; 
						line-height: 1.4;
						word-wrap: break-word;
						overflow-wrap: break-word;
						white-space: normal;
						flex: 1;
						min-width: 0;
					}
				`;
				document.head.appendChild(style);
			}
		}

		// Funzione per gestire i cambiamenti delle scelte
		function bindOnItemValueChange(question) {
			if (Array.isArray(question.choices)) {
				question.onItemValuePropertyChanged.add((_, evt) => {
					if (evt.propertyName === 'choices' && evt.name === 'text') {
						const item = evt.obj;
						const oldValue = item.value;  // Salva il vecchio value
						
						item.value = item.text;
						item.DisplayName = item.text;
						
						// Aggiorna deactivatableChoices se necessario
						if (question.deactivatableChoices && oldValue !== item.value) {
							const index = question.deactivatableChoices.indexOf(oldValue);
							if (index > -1) {
								question.deactivatableChoices[index] = item.value;
							}
						}
					}
				});
			}
		}

		function setupAdminCommentAndItemValue(survey, options, mode = 'normal') {
			// Applica markdown
			survey.onTextMarkdown.add(applyHtml);
			
			// Aggiungi stili CSS
			addAdminCommentStyles();
			
			// Funzione migliorata per aggiornare l'adminComment
			function updateAdminCommentForQuestion(question) {
				// Strategia multipla per trovare l'elemento della domanda
				const findQuestionElement = () => {
					// Prova diversi selettori in ordine di preferenza
					const selectors = [
						`[data-sv-question-name="${question.name}"]`,
						`[data-name="${question.name}"]`,
						`.sv-question[data-name="${question.name}"]`,
						`.svc-question__content[data-name="${question.name}"]`
					];
					
					for (const selector of selectors) {
						const elements = document.querySelectorAll(selector);
						if (elements.length > 0) {
							return Array.from(elements);
						}
					}
					
					// Fallback: cerca tutti gli elementi che potrebbero contenere la domanda
					const allQuestionElements = document.querySelectorAll('.sv-question, .svc-question__content, [data-sv-question-name]');
					return Array.from(allQuestionElements).filter(el => {
						const nameAttr = el.getAttribute('data-sv-question-name') || 
										el.getAttribute('data-name') || 
										el.querySelector('[data-name]')?.getAttribute('data-name');
						return nameAttr === question.name;
					});
				};
				
				// Prova più volte con timeout crescente per assicurarsi che il DOM sia aggiornato
				const attempts = [50, 150, 300];
				
				attempts.forEach((delay, index) => {
					setTimeout(() => {
						const questionElements = findQuestionElement();
						
						if (questionElements.length > 0) {
							console.log(`${mode.toUpperCase()} MODE - Found ${questionElements.length} elements for question ${question.name} at attempt ${index + 1}`);
							
							questionElements.forEach(element => {
								const container = element.closest('.sv-question') || 
												element.closest('.svc-question__content') || 
												element.parentElement ||
												element;
								renderAdminComment(question, container);
							});
						} else if (index === attempts.length - 1) {
							// Ultimo tentativo fallito, forza un refresh
							console.log(`${mode.toUpperCase()} MODE - Failed to find elements for ${question.name}, forcing survey refresh`);
							if (mode === 'restricted' && creator.survey) {
								creator.survey.render();
							}
						}
					}, delay);
				});
			}
			
			// Configura listener per adminComment
			survey.onAfterRenderQuestion.add((survey, renderOptions) => {
				const question = renderOptions.question;
				const htmlElement = renderOptions.htmlElement;
				
				console.log(`${mode.toUpperCase()} MODE - onAfterRenderQuestion for:`, question.name, 'adminComment:', question.adminComment);
				
				if (question.adminComment?.trim()) {
					renderAdminComment(question, htmlElement);
				}
				
				if (!question._adminCommentListenerAdded) {
					question.onPropertyChanged.add((sender, changeOptions) => {
						if (changeOptions.name === 'adminComment') {
							console.log(`${mode.toUpperCase()} MODE - adminComment changed for:`, question.name, 'new value:', changeOptions.newValue);
							
							// Usa la funzione migliorata per l'aggiornamento
							updateAdminCommentForQuestion(question);
							
							// Se siamo in modalità designer, forza anche un piccolo refresh
							if (mode === 'restricted' && creator && creator.survey) {
								// Forza un re-render parziale dopo un breve delay
								setTimeout(() => {
									try {
										// Triggerare un evento di refresh senza ricaricare tutto
										const event = new CustomEvent('surveyQuestionChanged', { 
											detail: { question: question.name } 
										});
										document.dispatchEvent(event);
									} catch (e) {
										console.log('Could not dispatch custom event:', e);
									}
								}, 100);
							}
						}
					});
					question._adminCommentListenerAdded = true;
				}
			});
			
			// Gestisci domande aggiunte dinamicamente (solo in designer)
			if (options.reason === "designer") {
				survey.onQuestionAdded.add((survey, addOptions) => {
					const question = addOptions.question;
					
					if (!question._adminCommentListenerAdded) {
						question.onPropertyChanged.add((sender, propertyOptions) => {
							if (propertyOptions.name === 'adminComment') {
								updateAdminCommentForQuestion(question);
							}
						});
						question._adminCommentListenerAdded = true;
					}
				});
			}
			
			// Configura gestione item value changes
			survey.getAllQuestions().forEach(bindOnItemValueChange);
			survey.onQuestionAdded.add((_, evt) => {
				bindOnItemValueChange(evt.question);
			});
			
			// Aggiungi un listener globale per forzare il refresh quando necessario
			if (mode === 'restricted') {
				document.addEventListener('surveyQuestionChanged', (e) => {
					const questionName = e.detail?.question;
					if (questionName && creator && creator.survey) {
						// Trova la domanda e forza un piccolo re-render
						const question = creator.survey.getQuestionByName(questionName);
						if (question && question.adminComment?.trim()) {
							setTimeout(() => {
								updateAdminCommentForQuestion(question);
							}, 200);
						}
					}
				});
			}
		}

		// INOLTRE: Aggiungi questa funzione helper migliorata per renderAdminComment
		function renderAdminComment(question, htmlElement) {
			if (!htmlElement) {
				console.log('renderAdminComment: htmlElement is null');
				return;
			}
			
			// Rimuovi eventuali admin comment esistenti
			htmlElement.querySelectorAll(".admin-comment").forEach(el => el.remove());
			
			if (!question.adminComment?.trim()) return;
			
			// Crea il nuovo elemento admin comment
			const div = document.createElement("div");
			div.className = "admin-comment";
			div.innerHTML = `
				<div class="admin-comment-content">
					<i class="material-icons-outlined">info</i>
					<div class="admin-comment-body">${question.adminComment}</div>
				</div>`;
			
			// Inserisci all'inizio dell'elemento host
			try {
				htmlElement.insertBefore(div, htmlElement.firstChild);
				console.log(`Successfully rendered adminComment for question: ${question.name}`);
			} catch (error) {
				console.log(`Error rendering adminComment for question ${question.name}:`, error);
				// Fallback: prova ad appendere alla fine
				try {
					htmlElement.appendChild(div);
				} catch (e) {
					console.log(`Fallback append also failed:`, e);
				}
			}
		}

		
		if (isRestrictedEditMode) {
			const style = document.createElement('style');
			style.innerHTML = `
				.svc-matrix-cell__question-controls-button {
					display: none;
				}
			`;
			document.head.appendChild(style);
			
			creatorOptions = {
				allowChangeThemeInPreview: false,
				allowEditExpressionsInTextEditor: true,
				allowModifyPages: false,
				showDefaultLanguageInPreviewTab: true,
				showDesignerTab: true,
				showErrorOnFailedSave: true,
				showInvisibleElementsInPreviewTab: false,
				showJSONEditorTab: false,
				showLogicTab: false,
				showObjectTitles: false,
				showPagesInPreviewTab: true,
				showPreviewTab: true,
				showSaveButton: true,
				showSidebar: false,
				showSimulatorInPreviewTab: false,
				showSurveyTitle: false,
				showThemeTab: false,
				showTitlesInExpressions: false,
				showTranslationTab: true,
				questionTypes: ["text", "checkbox", "radiogroup", "dropdown"]
			};

			creator = new SurveyCreator(creatorOptions);
			creator.toolboxLocation = "none";

			creator.survey.onTextMarkdown.add(applyHtml);
			
			creator.onElementAllowOperations.add(function (sender, options) {
				const question = options.obj;
				const isQuestionContainedInInitialJson = SurveyUtils.isQuestionNameInParsedJson(initialJson, question.name);

				if (isQuestionContainedInInitialJson) {
					options.allowDelete = false;
					options.allowChangeRequired = false;
					options.allowChangeType = false;
					options.allowChangeInputType = false;
				}
				options.allowCopy = false;
				options.allowDragging = false;
				options.allowShowSettings = true;
				if(options.obj.isPage) {
      				options.allowEdit = creator.survey.pages.indexOf(options.obj) == creator.survey.pageCount - 1;
				}
			});

			creator.onGetPropertyReadOnly.add(function(sender, options) {
				if (options.property.name === 'adminComment') {
					options.readOnly = !$hasRoleSysAdmin;
					return;
				}
    
				if (options.property.name === 'title') {
					const question = options.obj;
					const isQuestionContainedInInitialJson = SurveyUtils.isQuestionNameInParsedJson(initialJson, question.name);

					if (isQuestionContainedInInitialJson) {
						if (SurveyUtils.isChoices(question)) {
							const isDeactivatable = SurveyBuilderUtils.MODIFIABILITY_OPTIONS_FOR_DEACTIVATABLECHOICES.includes(question.modifiabilitySettingsForCompanyManager);
							if (isDeactivatable) {
								if (question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile e modificabile in ogni aspetto") {
									options.readOnly = false;
								} else {
									options.readOnly = true;
								}
							} else {
								options.readOnly = true;
							}
						} else if (SurveyUtils.isText(question)) {
							if (question.modifiabilitySettingsForCompanyManagerText === "Domanda disattivabile") {
								options.readOnly = false;
							} else {
								options.readOnly = true;
							}
						} else {
							options.readOnly = true;
						}
					} else {
						options.readOnly = false;
					}
				}
			});

			creator.onTranslationStringVisibility.add(function(sender, options) {
				if (options.propertyName === 'title') {
					const question = options.obj;
					const property = question.getPropertyByName('title');
					if (property && property.readOnly) {
						options.visible = false;
					}
				}
			});

			let whiteList = [ "text", "value", "Color", "DisplayName", "ChartTypeChoices", "ChartTypeMatrix", "ChartTypeNumber", 
			"OtherColor", "UnitOfMeasure", "PSCL", "Appendix", "isTimeRelated", "commentPlaceholder", "commentText", "dontKnowText", 
			"noneText", "otherPlaceHolder", "otherText", "validators", "requiredErrorText", "showDontKnowItem",
			"showRefuseItem", "rows", "description", "adminComment"];

			function updateWhiteList(question) {
				const isQuestionContainedInInitialJson = SurveyUtils.isQuestionNameInParsedJson(initialJson, question.name);
				
				if (SurveyUtils.isChoices(question)) {
					whiteList.push("choices");
					const isDeactivatable = SurveyBuilderUtils.MODIFIABILITY_OPTIONS_FOR_DEACTIVATABLECHOICES.includes(question.modifiabilitySettingsForCompanyManager);
					if (isDeactivatable) {
						if (question.deactivatableChoices.includes("other")) {
							whiteList.push("showOtherItem");
						}

						if (question.deactivatableChoices.includes("none")) {
							whiteList.push("showNoneItem");
						}
						if (question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile, alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta"
							|| question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile e modificabile in ogni aspetto"
						) {
							whiteList.push("visible");
							whiteList.push("visibleIf");
						}
					}
				} else if (SurveyUtils.isText(question)) {
					if (question.modifiabilitySettingsForCompanyManagerText === "Domanda disattivabile") {
						 whiteList.push("visible"); 
						 whiteList.push("visibleIf"); 
					}
				}
				
				if (!isQuestionContainedInInitialJson) {
					whiteList.push("modifiabilitySettingsForCompanyManager");
					whiteList.push("deactivatableChoices");
					whiteList.push("modifiabilitySettingsForCompanyManagerMatrix");
					whiteList.push("deactivatableRows");
					whiteList.push("modifiabilitySettingsForCompanyManagerText");
				}

				if (!$hasRoleCompanyManager) {
					whiteList.push("name");
				}
			}

			creator.onShowingProperty.add(function (sender, options) {
				const question = options.obj;
				const isQuestionContainedInInitialJson = SurveyUtils.isQuestionNameInParsedJson(initialJson, question.name);
				
				if (options.property.name === 'adminComment') {
					options.canShow = true;
					return;
				}
				
				if (isQuestionContainedInInitialJson) {
					updateWhiteList(question);
					options.canShow = whiteList.indexOf(options.property.name) > -1;
				} else {
					const basicProperties = ["text", "value", "Color", "DisplayName", "ChartTypeChoices", "ChartTypeMatrix", "ChartTypeNumber", 
						"OtherColor", "UnitOfMeasure", "PSCL", "Appendix", "isTimeRelated", "commentPlaceholder", "commentText", "dontKnowText", 
						"noneText", "otherPlaceHolder", "otherText", "validators", "requiredErrorText", "showDontKnowItem",
						"showRefuseItem", "rows", "description", "adminComment", "modifiabilitySettingsForCompanyManager",
						"deactivatableChoices", "modifiabilitySettingsForCompanyManagerMatrix", "deactivatableRows",
						"modifiabilitySettingsForCompanyManagerText"];
					
					if (!$hasRoleCompanyManager) {
						basicProperties.push("name");
					}
					
					options.canShow = basicProperties.indexOf(options.property.name) > -1;
				}
			});

			let settingsAction = creator.toolbar.actions.indexOf(creator.toolbar.actions.find(action => action.id === 'svd-settings'));   
			creator.toolbar.actions.splice(settingsAction, 1);

			creator.onPropertyEditorCreated.add((sender, options) => {
				if (options.property.name === "choices" || options.property.name === "rows") {
					options.editor.confirmDelete = true;
				}
			});

			 creator.onPropertyEditorUpdateTitleActions.add((sender, options) => {
				if(options.property.name === "choices" || options.property.name === "rows") {
					options.titleActions.length = 0;
				}
			});

			creator.onCollectionItemAllowOperations.add((sender, options) => {
				const question = options.obj;
				const item = options.item;
				const isQuestionContainedInInitialJson = SurveyUtils.isQuestionNameInParsedJson(initialJson, question.name);
				
				if (isQuestionContainedInInitialJson) {
					if (SurveyUtils.isChoices(question)) {
						const isChoiceContainedInInitialJson = SurveyUtils.isChoiceInParsedJson(initialJson, question.name, item.id);
						const isDeactivatable = SurveyBuilderUtils.MODIFIABILITY_OPTIONS_FOR_DEACTIVATABLECHOICES.includes(question.modifiabilitySettingsForCompanyManager);
						if (isDeactivatable) {
							if (question.deactivatableChoices.includes(item.id) || question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile e modificabile in ogni aspetto") {
								options.allowDelete = true;
								if (question.modifiabilitySettingsForCompanyManager !== "Domanda disattivabile e modificabile in ogni aspetto") {
									options.allowEdit = false;
								}
								if (!isChoiceContainedInInitialJson) {
										options.allowEdit = true;
									}
							} else {
								if (question.hasOther && item.id === "other") {
									options.allowDelete = false;
									options.allowEdit = false;
								} else if (question.hasNone && item.id === "none") {
									options.allowDelete = false;
									options.allowEdit = false;
								} else {
									options.allowDelete = !question.jsonObj.choices.some(choice => choice.value === item.id);
									options.allowEdit = false;
									if (!isChoiceContainedInInitialJson) {
										options.allowEdit = true;
									}
								}
							}
							
							options.allowAdd = ["Domanda disattivabile e modificabile in ogni aspetto", "Domanda disattivabile, alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta", "Alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta"].includes(question.modifiabilitySettingsForCompanyManager);
						} else {
							options.allowDelete = false;
							options.allowEdit = false;
						}
					} else if (SurveyUtils.isMatrix(question)) {
						const isDeactivatable = SurveyBuilderUtils.MODIFIABILITY_OPTIONS_MATRIX_FOR_DEACTIVATABLEROWS.includes(question.modifiabilitySettingsForCompanyManagerMatrix);
						if (isDeactivatable) {
							if (question.deactivatableRows.includes(item.id)) {
								options.allowDelete = true;
							} else {
								if (question.hasOther && item.id === "other") {
									options.allowDelete = false;
								} else if (question.hasNone && item.id === "none") {
									options.allowDelete = false;
								} else {
									options.allowDelete = !question.jsonObj.rows.some(row => row.value === item.id);
								}
							}
							options.allowAdd = false;
						} else {
							options.allowDelete = false;
						}
						options.allowDragging = false;
						options.allowCopy = false;
						options.allowEdit = false;
					}
				}
			});

			function createVisibleAction(question) {
				return new Action({
					id: "visible",
					css: new ComputedUpdater(() =>
						question.visible ? "sv-action-bar-item--secondary" : ""
					),
					innerCss: new ComputedUpdater(() =>
						question.visible ? "" : "btn-inactive"
					),
					title: "Visibile",
					iconName: new ComputedUpdater(() =>
						question.visible ? "icon-visible" : ""
					),
					iconSize: 16,
					action: () => {
						question.visible = !question.visible;
						if (!question._initialVisibleIf) {
							question._initialVisibleIf = question.visibleIf;
						}
						if (question.visible === false) {
							question.visibleIf = '';
						} else {
							question.visibleIf = question._initialVisibleIf;
						}
					},
					visibleIndex: 9
				});
			}

			creator.onSurveyInstanceCreated.add((sender, options) => {
				const survey = options.survey;
				
				// Setup comune usando la funzione helper
				setupAdminCommentAndItemValue(survey, options, 'restricted');
				
				// ======= LOGICA SPECIFICA PER MODALITÀ RESTRICTED =======
				if (options.reason == "designer") {
					survey.onShowingChoiceItem.add((sender, choiceOptions) => {
						const question = choiceOptions.question;
						const isQuestionContainedInInitialJson = SurveyUtils.isQuestionNameInParsedJson(initialJson, question.name);
				
						if (isQuestionContainedInInitialJson) {
							if (SurveyUtils.isChoices(question)) {
								if (!["Domanda disattivabile e modificabile in ogni aspetto", "Domanda disattivabile, alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta", "Alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta"].includes(question.modifiabilitySettingsForCompanyManager)) {
									if(choiceOptions.item == question.newItem) {
										choiceOptions.visible = false;
									}
								}
								if (choiceOptions.item == question.otherItem && !question.hasOther) {
									choiceOptions.visible = false;
								}
								if (choiceOptions.item == question.noneItem && !question.hasNone) {
									choiceOptions.visible = false;
								}
							}
						}
					});
				}

				if(options.area === "designer-tab") {
					survey.onGetQuestionTitleActions.add((_, titleOptions) => {
						const question = titleOptions.question;
						if (SurveyUtils.isChoices(question)) {
							const isDeactivatable = SurveyBuilderUtils.MODIFIABILITY_OPTIONS_FOR_DEACTIVATABLECHOICES.includes(question.modifiabilitySettingsForCompanyManager);
							if (isDeactivatable) {
								if (question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile, alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta"
									|| question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile e modificabile in ogni aspetto"
								) {
									const visibleAction = createVisibleAction(question);
									titleOptions.titleActions.push(visibleAction);
								}
							}
						} else if (SurveyUtils.isText(question)) {
							if (question.modifiabilitySettingsForCompanyManagerText === "Domanda disattivabile") {
								const visibleAction = createVisibleAction(question);
								titleOptions.titleActions.push(visibleAction);
							}
						}
					});
				}

				if(options.area === "translation-tab:table"){
					survey.onMatrixCellCreated.add((sender, cellOptions) => {
						if(cellOptions.columnName === "default"){
							cellOptions.cellQuestion.readOnly = true;
						}
					})
				}
			});
			
			creator.onDefineElementMenuItems.add((_, options) => {
				const question = options.obj;
				if (SurveyUtils.isChoices(question)) {
					const isDeactivatable = SurveyBuilderUtils.MODIFIABILITY_OPTIONS_FOR_DEACTIVATABLECHOICES.includes(question.modifiabilitySettingsForCompanyManager);
					if (isDeactivatable) {
						if (question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile, alcune opzioni di risposta disattivabili, possibilità di aggiungere opzioni di risposta"
							|| question.modifiabilitySettingsForCompanyManager === "Domanda disattivabile e modificabile in ogni aspetto") 
						{
							options.items.forEach((item) => {
								item.showTitle = false;
							});

							const visibleAction = createVisibleAction(question);
							options.items.push(visibleAction);
						}
					}
				} else if (SurveyUtils.isText(question)) {
					if (question.modifiabilitySettingsForCompanyManagerText === "Domanda disattivabile") {
						options.items.forEach((item) => {
							item.showTitle = false;
						});

						const visibleAction = createVisibleAction(question);
						options.items.push(visibleAction);
					}
				}
			});
			
		} else {
			const blackList = [ "storeOthersAsComment" ];

			creator.onGetPropertyReadOnly.add(function(sender, options) {
				if (options.property.name === 'adminComment') {
					options.readOnly = !$hasRoleSysAdmin;
				}
			});

			creator.onShowingProperty.add(function (_, options) {
				options.canShow = blackList.indexOf(options.property.name) < 0;
			});

			creator.onSurveyInstanceCreated.add((sender, options) => {
				setupAdminCommentAndItemValue(options.survey, options, 'normal');
			});
		}

		creator.onQuestionAdded.add(function(sender, options){
			var question = options.question;
			question.name = generateGUID()
			question.title = translations.pe.question.title || "Question";
			const isQuestionContainedInInitialJson = SurveyUtils.isQuestionNameInParsedJson(initialJson, question.name);

			if (SurveyUtils.isChoices(question)) {
				question.setPropertyValue("modifiabilitySettingsForCompanyManager", "Domanda non disattivabile né modificabile in alcun aspetto");
				question.otherText = translations.pe.otherText || "Other (Describe)";
				question.noneText = translations.pe.noneText || "No answer";
			} else if (SurveyUtils.isMatrix(question)) {
				question.setPropertyValue("modifiabilitySettingsForCompanyManagerMatrix", "Domanda non disattivabile né modificabile in alcun aspetto");
			} else if (SurveyUtils.isText(question)) {
				question.setPropertyValue("modifiabilitySettingsForCompanyManagerText", "Domanda non disattivabile né modificabile in alcun aspetto");
			}
			
			if (!isQuestionContainedInInitialJson && $hasRoleCompanyManager) {
				if (SurveyUtils.isChoices(question)) {
					question.setPropertyValue("modifiabilitySettingsForCompanyManager", "Domanda disattivabile e modificabile in ogni aspetto");
				} else if (SurveyUtils.isText(question)) {
					question.setPropertyValue("modifiabilitySettingsForCompanyManagerText", "Domanda disattivabile");
				}
			}

			if (isRestrictedEditMode) {
				const qIndex = question.visibleIndex;
				const questionCount = question.survey.getAllQuestions().length;
				if(qIndex != questionCount - 1){
					const page = question.page;
					page.removeQuestion(question);
					page.addElement(question);
				}
			}
		});

		creator.onItemValueAdded.add((sender, options) => {
			if (options.newItem && options.newItem.value) {
				const textValue = options.newItem.value;
				options.newItem.text = textValue;
				options.newItem.DisplayName = textValue;
			}
			
			const newItemsColor = options.obj.NewItemsColor;
			if (newItemsColor !== null && 
				newItemsColor !== undefined && 
				newItemsColor !== '' && 
				newItemsColor.toLowerCase() !== 'none') {
				options.newItem.Color = newItemsColor;
			}
			
			if (isRestrictedEditMode) {
				updateDeactivatableChoices(options.obj, options.newItem);
			}
		});

		function updateDeactivatableChoices(question, newItem) {
			if (!question.deactivatableChoices) {
				question.deactivatableChoices = [];
			}
			question.deactivatableChoices.push(newItem.value);
		}


		creator.saveSurveyFunc = () => {
			stringifiedJson = creator.text;
		};

		locale.subscribe(() => {
			if (document.getElementById('surveyCreator')) {
				localization.currentLocale = $locale;
				creator.render('surveyCreator');
				creator.text = stringifiedJson;
			}
		});

	});
</script>

<div id="surveyCreator"></div>